import { useEffect, useRef } from 'react'
import type { CrudFilter } from '@refinedev/core'
import type { TablePaginationConfig } from 'antd'

interface SyncTableWithUrlParams {
  /** Filters derived from the URL (tab, status, search…). */
  filters: CrudFilter[]
  /** Page derived from the URL. */
  urlPage: number
  /** refine's own page state. */
  currentPage: number
  setFilters: (filters: CrudFilter[], behavior: 'replace') => void
  setCurrentPage: (page: number) => void
}

/**
 * Keeps refine's `useTable` state in sync with the URL.
 *
 * The URL is the single source of truth for list state. Seeding `useState` from the URL once
 * and then writing the URL back from that state made browser Back a no-op (the effect
 * immediately restored the old query string) and made clicking the same menu entry with
 * different params do nothing. Pushing URL changes into the table instead fixes both.
 */
export function useSyncTableWithUrl({
  filters,
  urlPage,
  currentPage,
  setFilters,
  setCurrentPage,
}: SyncTableWithUrlParams): void {
  const serializedFilters = JSON.stringify(filters)
  const appliedFiltersRef = useRef(serializedFilters)

  useEffect(() => {
    if (appliedFiltersRef.current === serializedFilters) return
    appliedFiltersRef.current = serializedFilters
    setFilters(filters, 'replace')
  }, [serializedFilters, filters, setFilters])

  useEffect(() => {
    if (currentPage === urlPage) return
    setCurrentPage(urlPage)
  }, [urlPage, currentPage, setCurrentPage])
}

/**
 * Routes antd table pagination clicks through the URL so Back/Forward work.
 * refine's own `tableProps.onChange` still runs, so both stay in sync.
 */
export function bindPaginationToUrl(
  pagination: TablePaginationConfig | false | undefined,
  urlPage: number,
  setPage: (page: number) => void,
): TablePaginationConfig | false {
  if (!pagination) return false
  return {
    ...pagination,
    current: urlPage,
    onChange: (page: number) => setPage(page),
  }
}
